// SearchPill Component - Airbnb-Style Pill Search Bar with location display

import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin } from 'lucide-react';
import { LocationAutocomplete } from '../location/LocationAutocomplete';
import type { GeocodedLocation } from '../../types';

interface SearchPillProps {
  value?: string;
  onChange?: (value: string) => void;
  scrolled?: boolean;
  placeholder?: string;
  // Location
  selectedLocation?: string;
  onLocationSelect?: (location: GeocodedLocation) => void;
  onNearbyClick?: () => void;
  isLocating?: boolean;
  nearbyAddress?: string;
}

export function SearchPill({
  value = '',
  onChange,
  scrolled = false,
  placeholder = 'Wofuer?',
  selectedLocation,
  onLocationSelect,
  onNearbyClick,
  isLocating = false,
  nearbyAddress,
}: SearchPillProps) {
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locationValue, setLocationValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setShowLocationDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationSelect = (location: GeocodedLocation) => {
    if (onLocationSelect) {
      onLocationSelect(location);
    }
    setLocationValue('');
    setShowLocationDropdown(false);
  };

  const handleNearbyClick = () => {
    if (onNearbyClick) {
      onNearbyClick();
    }
    setShowLocationDropdown(false);
  };

  const displayLocation = selectedLocation || 'Irgendwo';

  return (
    <div className="relative">
      <div
        className={`flex items-center border border-gray-300 rounded-full shadow-sm hover:shadow-md transition cursor-pointer divide-x divide-gray-300 py-2.5 ${
          scrolled ? 'scale-95' : 'scale-100'
        }`}
      >
        {/* Location Section - clickable */}
        <div
          ref={triggerRef}
          onClick={() => setShowLocationDropdown(!showLocationDropdown)}
          className="px-6 text-sm font-semibold text-gray-800 hover:bg-gray-50 rounded-l-full flex items-center gap-2"
        >
          <MapPin className="w-4 h-4 text-gray-500" />
          <span className="truncate max-w-24">{displayLocation}</span>
        </div>
        <div className="px-6 text-sm font-semibold text-gray-800 hover:bg-gray-50">
          Jederzeit
        </div>
        <div className="pl-6 pr-2 flex items-center gap-3 text-sm text-gray-600 hover:bg-gray-50 rounded-r-full">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder}
            className={`bg-transparent outline-none w-24 ${
              value ? 'text-gray-900' : 'text-gray-400'
            }`}
          />
          <div className="bg-indigo-500 p-2 rounded-full text-white">
            <Search className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* Location Dropdown */}
      {showLocationDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 mt-2 w-80 z-50 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
        >
          <div className="p-3 border-b border-gray-100">
            <LocationAutocomplete
              value={locationValue}
              onChange={setLocationValue}
              onSelect={handleLocationSelect}
              placeholder="Ort suchen..."
              showNearbyButton={true}
              onNearbyClick={handleNearbyClick}
              isLocating={isLocating}
              nearbyAddress={nearbyAddress}
              className=""
              inputClassName="border border-gray-200 shadow-sm"
            />
            {/* Direct text search button */}
            {locationValue.trim().length >= 2 && (
              <button
                onClick={() => {
                  const location: GeocodedLocation = {
                    latitude: 0,
                    longitude: 0,
                    formattedAddress: locationValue.trim(),
                    city: locationValue.trim(),
                  };
                  handleLocationSelect(location);
                }}
                className="w-full mt-2 flex items-center gap-2 p-2 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition text-left text-sm"
              >
                <Search className="w-4 h-4 text-indigo-600" />
                <span className="text-indigo-700 font-medium">
                  Orte in "{locationValue.trim()}" suchen
                </span>
              </button>
            )}
          </div>
          {/* Quick city selection */}
          <div className="p-2 max-h-48 overflow-y-auto">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 py-1">
              Beliebte Orte
            </div>
            {[
              'Muenchen',
              'Berlin',
              'Hamburg',
              'Garmisch-Partenkirchen',
              'Donauwoerth',
              'Augsburg',
            ].map((city) => (
              <button
                key={city}
                onClick={() => {
                  const location: GeocodedLocation = {
                    latitude: 0,
                    longitude: 0,
                    formattedAddress: city,
                    city: city,
                  };
                  handleLocationSelect(location);
                }}
                className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-50 transition text-left text-sm"
              >
                <MapPin className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">{city}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchPill;
